import { useMemo, useState } from "react";
import { ChoiceField } from "@/components/tool/CalcFields";
import { applyFraction, type FractionOp } from "@/lib/math/education";
import { m } from "@/paraglide/messages";

const OPS: { value: FractionOp; sign: string }[] = [
  { value: "add", sign: "+" },
  { value: "subtract", sign: "−" },
  { value: "multiply", sign: "×" },
  { value: "divide", sign: "÷" },
];

function FractionInput({
  label,
  n,
  d,
  onN,
  onD,
}: {
  label: string;
  n: string;
  d: string;
  onN: (v: string) => void;
  onD: (v: string) => void;
}) {
  return (
    <div className="flex flex-col items-center">
      <input
        type="number"
        aria-label={`${label} numerator`}
        value={n}
        onChange={(e) => onN(e.target.value)}
        className="w-20 rounded-md border border-black/15 bg-white px-2 py-2 text-center text-lg text-neutral-900"
      />
      <span className="my-1 h-px w-16 bg-current opacity-40" />
      <input
        type="number"
        aria-label={`${label} denominator`}
        value={d}
        onChange={(e) => onD(e.target.value)}
        className="w-20 rounded-md border border-black/15 bg-white px-2 py-2 text-center text-lg text-neutral-900"
      />
    </div>
  );
}

export function FractionCalc() {
  const [a, setA] = useState({ n: "1", d: "2" });
  const [b, setB] = useState({ n: "1", d: "3" });
  const [op, setOp] = useState<FractionOp>("add");

  const out = useMemo(() => {
    const an = Number(a.n);
    const ad = Number(a.d);
    const bn = Number(b.n);
    const bd = Number(b.d);
    if (![an, ad, bn, bd].every(Number.isFinite) || ad === 0 || bd === 0)
      return null;
    const r = applyFraction({ n: an, d: ad }, { n: bn, d: bd }, op);
    if (!Number.isFinite(r.n) || r.d === 0) return null;
    return r;
  }, [a, b, op]);

  const sign = OPS.find((o) => o.value === op)?.sign ?? "+";

  return (
    <div className="sci-card rounded-2xl space-y-5 p-6 sm:p-8">
      <div className="flex flex-wrap items-center justify-center gap-4">
        <FractionInput
          label={m.fr_first()}
          n={a.n}
          d={a.d}
          onN={(v) => setA((s) => ({ ...s, n: v }))}
          onD={(v) => setA((s) => ({ ...s, d: v }))}
        />
        <span className="text-2xl font-semibold">{sign}</span>
        <FractionInput
          label={m.fr_second()}
          n={b.n}
          d={b.d}
          onN={(v) => setB((s) => ({ ...s, n: v }))}
          onD={(v) => setB((s) => ({ ...s, d: v }))}
        />
      </div>

      <ChoiceField
        label={m.fr_operation()}
        value={op}
        onChange={setOp}
        options={OPS.map((o) => ({
          value: o.value,
          label: m[`fr_${o.value}` as "fr_add"](),
        }))}
      />

      {out ? (
        <div className="sci-result-zone flex items-center justify-between gap-4 rounded-xl px-5 py-4">
          <span className="text-sm font-medium text-muted-foreground">
            {m.fr_label()}
          </span>
          <span
            data-testid="fr-result"
            className="sci-result font-display text-3xl font-bold sm:text-4xl"
          >
            {out.d === 1 ? `${out.n}` : `${out.n}/${out.d}`}
          </span>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{m.fr_hint()}</p>
      )}
    </div>
  );
}
